import { useState, useEffect } from 'react';
import { LuUsers, LuPlus, LuBuilding2, LuMail, LuShield, LuPencil, LuTrash2, LuX, LuLock, LuKey, LuRefreshCw, LuEye, LuEyeOff, LuLanguages, LuPhone } from 'react-icons/lu';
import { FiCheckCircle, FiMinusCircle } from 'react-icons/fi';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { ROLES, canDeleteUser, getRoleLabel, getRoleBadgeColor, isSuperadmin, isAdministrator } from '../utils/roleCheck';
import { sendInvitationEmail } from '../utils/notificationHelpers';

export default function UserManagement() {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [showPasswordText, setShowPasswordText] = useState(false);
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    role: ROLES.GENERAL_USER,
    property_id: '',
    building_id: '',
    unit: '',
    is_activated: true,
    new_password: '',
    language: 'en',
    phone_number: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const generatePassword = () => {
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let retVal = "";
    for (let i = 0, n = charset.length; i < 12; ++i) {
      retVal += charset.charAt(Math.floor(Math.random() * n));
    }
    return retVal;
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersRes, propsRes, bldgsRes, prefsRes] = await Promise.all([
      window.ezsite.apis.tablePage(70648, { PageNo: 1, PageSize: 100, OrderByField: 'name', IsAsc: true }),
      window.ezsite.apis.tablePage(70646, { PageNo: 1, PageSize: 100 }),
      window.ezsite.apis.tablePage(70647, { PageNo: 1, PageSize: 500 }),
      window.ezsite.apis.tablePage(70673, { PageNo: 1, PageSize: 500 })]
      );

      const profileList = usersRes.data?.List || [];
      const prefList = prefsRes.data?.List || [];

      const mergedUsers = profileList.map((profile) => {
        const pEmail = (profile.email || profile.Email || '').toLowerCase();
        const profileId = profile.id || profile.ID;
        const userPref = prefList.find((p) => Number(p.user_id) === Number(profileId));

        return {
          ...profile,
          profile_id: profileId,
          is_activated: profile.is_active ?? profile.IsActive ?? true,
          email: pEmail,
          language: userPref?.language || profile.language || profile.Language || 'en'
        };
      });

      setUsers(mergedUsers);
      setProperties(propsRes.data?.List || []);
      setBuildings(bldgsRes.data?.List || []);
    } catch (error) {
      console.error('Failed to load user data:', error);
      toast.error(t('userManagement.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);

    try {
      const email = userForm.email.toLowerCase().trim();
      const password = userForm.new_password.trim();

      // Validate phone number format if provided
      if (userForm.phone_number && userForm.phone_number.trim()) {
        const phoneRegex = /^[\d\s\-+()]+$/;
        if (!phoneRegex.test(userForm.phone_number.trim())) {
          toast.error('Please enter a valid phone number');
          setSubmitting(false);
          return;
        }
      }

      const profileData = {
        name: userForm.name,
        email: email,
        Email: email,
        role: userForm.role,
        property_id: userForm.property_id ? Number(userForm.property_id) : null,
        building_id: userForm.building_id ? Number(userForm.building_id) : null,
        unit: userForm.unit,
        is_active: userForm.is_activated,
        language: userForm.language,
        phone_number: userForm.phone_number || ''
      };

      if (password) {
        profileData.password = password;
      }

      let targetUserId = null;
      let isNewUser = false;

      if (editingUser) {
        targetUserId = Number(editingUser.profile_id);
        await window.ezsite.apis.tableUpdate(70648, { id: targetUserId, ...profileData });
        toast.success(t('userManagement.successUpdate'));
      } else {
        if (!password) {
          toast.error(t('userManagement.passwordRequired'));
          setSubmitting(false);
          return;
        }

        isNewUser = true;
        console.log('[UserManagement] Creating new user profile in table 70648...');
        const createRes = await window.ezsite.apis.tableCreate(70648, profileData);
        if (createRes.error) throw createRes.error;

        // Comprehensive ID extraction from EasySite response
        const createdRecord = createRes.data?.data || createRes.data || createRes;
        targetUserId = createdRecord?.id || createdRecord?.ID || (typeof createdRecord === 'number' ? createdRecord : null);

        console.log('[UserManagement] User created. Extracted targetUserId:', targetUserId);

        if (!targetUserId) {
          console.warn('[UserManagement] Could not extract ID from create response. Response was:', createRes);
        }

        toast.success(t('userManagement.successCreate'));
      }

      // Sync preferences and send email
      if (targetUserId && !isNaN(targetUserId)) {
        try {
          const { data: prefRes } = await window.ezsite.apis.tablePage(70673, {
            PageNo: 1,
            PageSize: 1,
            Filters: [{ name: 'user_id', op: 'Equal', value: Number(targetUserId) }]
          });

          if (prefRes?.List?.[0]) {
            await window.ezsite.apis.tableUpdate(70673, {
              id: prefRes.List[0].id,
              language: userForm.language
            });
          } else {
            await window.ezsite.apis.tableCreate(70673, {
              user_id: Number(targetUserId),
              language: userForm.language,
              notify_on_mentions: true,
              notify_property_topics: false,
              notify_building_topics: false,
              notify_topic_replies: true,
              notify_property_faults: false,
              notify_building_faults: false,
              notify_fault_status: true,
              notify_announcements: true,
              email_notifications: true
            });
          }
        } catch (prefError) {
          console.error('[UserManagement] Failed to sync language preference:', prefError);
        }

        // Send invitation email for new users
        if (isNewUser && password) {
          console.log(`[UserManagement] Triggering invitation email for ${email}`);
          try {
            const emailSent = await sendInvitationEmail(email, userForm.name, password, userForm.language);
            if (emailSent) {
              console.log('[UserManagement] Invitation email sent successfully');
            } else {
              console.warn('[UserManagement] sendInvitationEmail returned failure status');
            }
          } catch (emailError) {
            console.error('[UserManagement] Failed to trigger invitation email process:', emailError);
          }
        }
      } else if (isNewUser) {
        console.error('[UserManagement] User created but targetUserId is missing. Attempting email sending as fallback if email is valid...');
        // Fallback for email sending if ID extraction failed but user was created
        if (email && password) {
          await sendInvitationEmail(email, userForm.name, password, userForm.language).catch((err) => {
            console.error('[UserManagement] Fallback email sending also failed:', err);
          });
        }
      }

      closeModal();
      loadData();
    } catch (error) {
      console.error('Operation failed:', error);
      toast.error(error.message || t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(t('userManagement.deleteConfirm'))) return;

    try {
      setSubmitting(true);
      const profileId = user.profile_id;

      // Delete profile from 70648
      const { error } = await window.ezsite.apis.tableDelete(70648, profileId);
      if (error) throw error;

      // Also cleanup preferences if they exist
      try {
        const { data: prefRes } = await window.ezsite.apis.tablePage(70673, {
          PageNo: 1,
          PageSize: 1,
          Filters: [{ name: 'user_id', op: 'Equal', value: Number(profileId) }]
        });
        if (prefRes?.List?.[0]) {
          await window.ezsite.apis.tableDelete(70673, prefRes.List[0].id);
        }
      } catch (e) {
        console.warn('[UserManagement] Failed to cleanup preferences for deleted user:', e);
      }

      toast.success(t('userManagement.successDelete'));
      loadData();
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error(t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditClick = (user) => {
    setEditingUser(user);
    setShowPasswordFields(false);
    setShowPasswordText(false);
    setUserForm({
      name: user.name || user.Name || '',
      email: user.email || user.Email || '',
      role: user.role || user.Role || ROLES.GENERAL_USER,
      property_id: user.property_id || user.PropertyID || '',
      building_id: user.building_id || user.BuildingID || '',
      unit: user.unit || user.Unit || '',
      is_activated: user.is_activated,
      new_password: '',
      language: user.language || 'en',
      phone_number: user.phone_number || user.PhoneNumber || ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setShowPasswordFields(false);
    setShowPasswordText(false);
    setUserForm({ name: '', email: '', role: ROLES.GENERAL_USER, property_id: '', building_id: '', unit: '', is_activated: true, new_password: '', language: 'en', phone_number: '' });
  };

  const availableRoles = Object.values(ROLES).filter((role) => {
    if (currentUser.role === ROLES.SUPERADMIN) return true;
    if (currentUser.role === ROLES.ADMINISTRATOR) return role !== ROLES.SUPERADMIN;
    return false;
  });

  if (loading) return (
    <div className="flex justify-center p-12">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>);


  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-green-50 rounded-lg">
            <LuUsers className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('userManagement.title')}</h1>
            <p className="text-sm text-gray-600">{t('userManagement.subtitle')}</p>
          </div>
        </div>
        <button onClick={() => {
          setEditingUser(null);
          setUserForm({ name: '', email: '', role: ROLES.GENERAL_USER, property_id: '', building_id: '', unit: '', is_activated: true, new_password: generatePassword(), language: 'en', phone_number: '' });
          setShowPasswordFields(true);
          setShowPasswordText(true);
          setShowModal(true);
        }} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
          <LuPlus className="w-4 h-4" /> {t('userManagement.addUser')}
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <table className="w-full text-left border-collapse">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-4 text-sm font-semibold text-gray-900">{t('userManagement.user')}</th>
              <th className="px-6 py-4 text-sm font-semibold text-gray-900">Phone</th>
              <th className="px-6 py-4 text-sm font-semibold text-gray-900">{t('userManagement.status')}</th>
              <th className="px-6 py-4 text-sm font-semibold text-gray-900">{t('userManagement.role')}</th>
              <th className="px-6 py-4 text-sm font-semibold text-gray-900">{t('userManagement.affiliation')}</th>
              <th className="px-6 py-4 text-sm font-semibold text-gray-900 text-right">{t('userManagement.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {users.map((u) => {
              const propId = u.property_id || u.PropertyID;
              const bldgId = u.building_id || u.BuildingID;
              const prop = properties.find((p) => p.id === propId);
              const bldg = buildings.find((b) => b.id === bldgId);
              const canModify = canDeleteUser(currentUser.role, u.role || u.Role);
              const isSelf = Number(u.profile_id) === Number(currentUser.id);

              return (
                <tr key={u.profile_id} className="hover:bg-gray-50 transition">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold">
                        {(u.name || u.Name || 'U').charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{u.name || u.Name}</div>
                        <div className="text-xs text-gray-500">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1.5 text-sm text-gray-700">
                      {u.phone_number || u.PhoneNumber ?
                      <>
                          <LuPhone className="w-3.5 h-3.5 text-gray-400" />
                          <span>{u.phone_number || u.PhoneNumber}</span>
                        </> :

                      <span className="text-gray-400 text-xs">N/A</span>
                      }
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {u.is_activated ?
                    <span className="flex items-center gap-1.5 text-xs font-bold text-green-600 bg-green-50 px-2 py-1 rounded-full border border-green-100">
                        <FiCheckCircle className="w-3.5 h-3.5" /> {t('userManagement.active')}
                      </span> :
                    <span className="flex items-center gap-1.5 text-xs font-bold text-gray-500 bg-gray-50 px-2 py-1 rounded-full border border-gray-200">
                        <FiMinusCircle className="w-3.5 h-3.5" /> {t('userManagement.inactive')}
                      </span>
                    }
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold border ${getRoleBadgeColor(u.role || u.Role)}`}>
                      {getRoleLabel(u.role || u.Role)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{prop?.name || 'Global'}</div>
                    <div className="text-xs text-gray-500">
                      {bldg?.name || t('userManagement.allBuildings')} {u.unit && `• ${u.unit}`}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {canModify &&
                      <>
                          <button onClick={() => handleEditClick(u)} className="p-2 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-blue-50 transition">
                            <LuPencil className="w-4 h-4" />
                          </button>
                          {!isSelf &&
                        <button
                          onClick={() => handleDeleteUser(u)}
                          className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition"
                          title={t('common.delete')}>

                              <LuTrash2 className="w-4 h-4" />
                            </button>
                        }
                        </>
                      }
                      {!canModify && !isSelf &&
                      <span className="text-xs text-gray-400 italic">{t('userManagement.protected')}</span>
                      }
                      {isSelf &&
                      <span className="text-xs text-blue-500 font-medium px-2 py-1 bg-blue-50 rounded-md border border-blue-100 italic">{t('faultReports.you')}</span>
                      }
                    </div>
                  </td>
                </tr>);

            })}
          </tbody>
        </table>
      </div>

      {showModal &&
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-8 shadow-2xl relative animate-in zoom-in duration-200">
            <button onClick={closeModal} className="absolute top-6 right-6 p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-100">
              <LuX className="w-5 h-5" />
            </button>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{editingUser ? t('userManagement.updateUser') : t('userManagement.addNewUser')}</h2>
            <form onSubmit={handleSaveUser} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">{t('userManagement.fullName')}</label>
                  <input required className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 outline-none focus:ring-2 focus:ring-blue-500 transition"
                value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">{t('userManagement.emailAddress')}</label>
                  <input required type="email" className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 outline-none focus:ring-2 focus:ring-blue-500 transition"
                value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} />
                </div>
                
                <div className="col-span-2">
                  <label className="block text-sm font-bold text-gray-700 mb-1.5 flex items-center gap-2">
                    <LuPhone className="w-4 h-4" /> Phone Number
                  </label>
                  <input
                  type="tel"
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 outline-none focus:ring-2 focus:ring-blue-500 transition"
                  value={userForm.phone_number}
                  onChange={(e) => setUserForm({ ...userForm, phone_number: e.target.value })}
                  placeholder="Enter phone number (optional)" />

                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-bold text-gray-700 mb-1.5 flex items-center gap-2">
                    <LuLanguages className="w-4 h-4" /> {t('settings.languagePreference')}
                  </label>
                  <select
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 outline-none focus:ring-2 focus:ring-blue-500 transition"
                  value={userForm.language}
                  onChange={(e) => setUserForm({ ...userForm, language: e.target.value })}>

                    <option value="en">English</option>
                    <option value="el">Ελληνικά (Greek)</option>
                  </select>
                </div>

                <div className="col-span-2 p-4 bg-gray-50 rounded-xl border border-gray-200 flex items-center justify-between">
                  <div>
                    <p className="font-bold text-gray-900 text-sm">{t('userManagement.accountStatus')}</p>
                    <p className="text-xs text-gray-500">{t('userManagement.allowLogin')}</p>
                  </div>
                  <button type="button" onClick={() => setUserForm({ ...userForm, is_activated: !userForm.is_activated })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${userForm.is_activated ? 'bg-green-600' : 'bg-gray-300'}`}>
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${userForm.is_activated ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
                <div className="col-span-2">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-bold text-gray-700">{t('userManagement.security')}</label>
                    <div className="flex items-center gap-2">
                      {!editingUser &&
                    <button
                      type="button"
                      onClick={() => setUserForm({ ...userForm, new_password: generatePassword() })}
                      className="text-xs font-bold text-blue-600 hover:underline flex items-center gap-1">

                          <LuRefreshCw className="w-3 h-3" /> {t('common.update')}
                        </button>
                    }
                      {editingUser &&
                    <button type="button" onClick={() => setShowPasswordFields(!showPasswordFields)} className="text-xs font-bold text-blue-600 hover:underline flex items-center gap-1">
                          <LuKey className="w-3 h-3" /> {showPasswordFields ? t('userManagement.cancelReset') : t('userManagement.resetPassword')}
                        </button>
                    }
                    </div>
                  </div>
                  {showPasswordFields &&
                <div className="relative">
                      <input
                    type={showPasswordText ? "text" : "password"}
                    required
                    className="w-full bg-blue-50 border border-blue-200 rounded-xl px-4 py-2.5 outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                    placeholder={t('userManagement.passwordPlaceholder')}
                    value={userForm.new_password}
                    onChange={(e) => setUserForm({ ...userForm, new_password: e.target.value })} />

                      <button
                    type="button"
                    onClick={() => setShowPasswordText(!showPasswordText)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">

                        {showPasswordText ? <LuEyeOff className="w-4 h-4" /> : <LuEye className="w-4 h-4" />}
                      </button>
                    </div>
                }
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">{t('userManagement.systemRole')}</label>
                  <select className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 outline-none focus:ring-2 focus:ring-blue-500"
                value={userForm.role} onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}>
                    {availableRoles.map((r) => <option key={r} value={r}>{getRoleLabel(r)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">{t('userManagement.property')}</label>
                  <select className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 outline-none"
                value={userForm.property_id} onChange={(e) => setUserForm({ ...userForm, property_id: e.target.value })}>
                    <option value="">{t('userManagement.selectProperty')}</option>
                    {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">{t('userManagement.building')}</label>
                  <select className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 outline-none"
                value={userForm.building_id} onChange={(e) => setUserForm({ ...userForm, building_id: e.target.value })}>
                    <option value="">{t('userManagement.allBuildings')}</option>
                    {buildings.filter((b) => b.property_id === Number(userForm.property_id)).map((b) =>
                  <option key={b.id} value={b.id}>{b.name}</option>
                  )}
                  </select>
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={closeModal} className="flex-1 px-4 py-3 border border-gray-200 rounded-xl font-bold text-gray-600 hover:bg-gray-50">{t('common.cancel')}</button>
                <button type="submit" disabled={submitting} className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 disabled:opacity-50">
                  {submitting ? t('userManagement.processing') : t('userManagement.saveChanges')}
                </button>
              </div>
            </form>
          </div>
        </div>
      }
    </div>);

}